/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';

import { Alert, Box, capitalize, CircularProgress, InputAdornment, Stack } from '@mui/material';
import { useTheme } from '../../styles';
import { AutocompleteContainer, AutocompleteInput, AutocompletePopper, AutocompletePopperPortal } from './Base';
import { KeyOutlined } from '@mui/icons-material';
import { SecretListItem } from './ListItem/SecretListItem';
import { useParams } from 'react-router-dom';
import { usePermissions } from '@common/hooks/usePermissions';
import { Resource } from '@store/types';
import { ErrorMessage } from './Models';
import { IntegrationSecret } from '@requests/secrets';

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  loading?: boolean;
  secrets: IntegrationSecret[];
  placeholder?: string;
  portal?: boolean;
  refetch: () => void;
  error?: string;
}

export const Secrets: React.FC<Props> = (props) => {
  const autocompleteRef = useRef(null);
  const theme = useTheme();

  const { service_id } = useParams();
  const { admin, permissions } = usePermissions(service_id as string, Resource.SECRET);
  const { value, onChange, disabled, secrets = [], placeholder, loading } = props;

  const [open, setOpen] = useState<boolean>(false);

  const allowRead = admin || permissions?.read;

  if (!allowRead) {
    return (
      <Alert severity='error'>
        You do not have permission to view secrets in this workspace. If you think this is a mistake, ask your workspace
        admin to give you permission.
      </Alert>
    );
  }

  const labels = secrets.map((secret) => ({
    id: secret.id,
    label: secret.identifier,
    value: secret,
    metadata: {
      group: `${capitalize(secret.type)} Secrets`,
      key: secret.identifier,
    },
  }));

  return (
    <Stack spacing={1}>
      <AutocompleteContainer open={open}>
        <Autocomplete
          value={labels.find((item) => item.label === value)}
          ref={autocompleteRef}
          onChange={(event, newValue) => {
            onChange(event, newValue);
            setOpen(false);
            (document?.activeElement as HTMLElement)?.blur();
          }}
          disabled={disabled}
          PopperComponent={props?.portal ? AutocompletePopperPortal : AutocompletePopper}
          groupBy={(option) => option?.metadata?.group}
          renderOption={(props, option) => <SecretListItem {...props} option={option} />}
          autoHighlight
          options={labels}
          disablePortal
          getOptionLabel={(option) => option?.label || ''}
          renderInput={(params) => (
            <AutocompleteInput
              open={open}
              error={Boolean(props?.error)}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
              startAdornment={
                value &&
                !loading && (
                  <InputAdornment position='start'>
                    <Box
                      component='span'
                      sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        borderRadius: '3px',
                        mr: 0.35,
                        ml: 0.31,
                        mt: '0px',
                      }}
                    >
                      <KeyOutlined sx={{ fontSize: '20px', color: theme.primary }} />
                    </Box>
                  </InputAdornment>
                )
              }
            />
          )}
        />
      </AutocompleteContainer>
      {props?.error && <ErrorMessage>{props.error}</ErrorMessage>}
    </Stack>
  );
};
